import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { Button } from '../ui/button';

const VIEWPORT_GAP = 16;
const CARD_WIDTH = 360;

const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

const MetaAgentTour = ({ open, sectionLabel, steps = [], onClose }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const cardRef = useRef(null);

  const step = steps[stepIndex];

  useEffect(() => {
    if (open) setStepIndex(0);
  }, [open, sectionLabel]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight') {
        setStepIndex((current) => Math.min(current + 1, steps.length - 1));
      }
      if (event.key === 'ArrowLeft') {
        setStepIndex((current) => Math.max(current - 1, 0));
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, open, steps.length]);

  useLayoutEffect(() => {
    if (!open || !step) return undefined;
    const findTarget = () => {
      const exactTarget = step.target ? document.querySelector(step.target) : null;
      if (exactTarget) return exactTarget;
      const fallbackSelector = step.fallbackTarget || '[data-tour="meta-agent-panel"]';
      return document.querySelector(fallbackSelector);
    };
    const target = findTarget();
    if (target) target.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });

    const updatePosition = () => {
      const activeTarget = findTarget();
      if (!activeTarget) {
        setTargetRect(null);
        return;
      }
      const rect = activeTarget.getBoundingClientRect();
      const top = clamp(rect.top - 6, 8, window.innerHeight - 24);
      const left = clamp(rect.left - 6, 8, window.innerWidth - 24);
      setTargetRect({
        top,
        left,
        width: Math.max(24, Math.min(rect.width + 12, window.innerWidth - left - 8)),
        height: Math.max(24, Math.min(rect.height + 12, window.innerHeight - top - 8)),
      });
    };

    const timer = window.setTimeout(updatePosition, 250);
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, step]);

  if (!open || !step || typeof document === 'undefined') return null;

  const cardHeight = cardRef.current?.offsetHeight || 250;
  const renderedCardWidth = Math.min(CARD_WIDTH, window.innerWidth - (VIEWPORT_GAP * 2));
  const maximumCardLeft = Math.max(VIEWPORT_GAP, window.innerWidth - renderedCardWidth - VIEWPORT_GAP);
  const cardLeft = targetRect
    ? clamp(targetRect.left + (targetRect.width / 2) - (renderedCardWidth / 2), VIEWPORT_GAP, maximumCardLeft)
    : clamp((window.innerWidth - renderedCardWidth) / 2, VIEWPORT_GAP, maximumCardLeft);
  const roomBelow = targetRect && targetRect.top + targetRect.height + cardHeight + 24 < window.innerHeight;
  const cardTop = targetRect
    ? (roomBelow
      ? targetRect.top + targetRect.height + 12
      : Math.max(VIEWPORT_GAP, targetRect.top - cardHeight - 12))
    : Math.max(VIEWPORT_GAP, (window.innerHeight - cardHeight) / 2);
  const isLastStep = stepIndex === steps.length - 1;

  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-[100]" role="dialog" aria-label={`${sectionLabel} guided tour`}>
      {!targetRect && <div className="absolute inset-0 bg-slate-950/75" />}
      {targetRect && (
        <div
          className="pointer-events-none fixed rounded-2xl border-2 border-indigo-400 transition-all duration-200"
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
            boxShadow: '0 0 0 9999px rgba(2, 6, 23, 0.76), 0 0 0 5px rgba(99, 102, 241, 0.18)',
          }}
        />
      )}
      <div
        ref={cardRef}
        className="pointer-events-auto fixed w-[calc(100vw-2rem)] max-w-[360px] rounded-2xl border border-indigo-400/40 bg-[var(--tg-surface)] p-5 text-[var(--tg-text-primary)] shadow-2xl"
        style={{ top: cardTop, left: cardLeft }}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-indigo-400">{sectionLabel} tour</p>
            <h2 className="mt-1 text-lg font-semibold">{step.title}</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-[var(--tg-text-muted)] hover:bg-[var(--tg-surface-raised)]" aria-label="Skip tour">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mt-3 whitespace-pre-line text-sm leading-6 text-[var(--tg-text-secondary)]">{step.description}</p>
        {step.tip && <p className="mt-3 rounded-xl bg-indigo-500/10 px-3 py-2 text-xs leading-5 text-indigo-300">Tip: {step.tip}</p>}
        <p className="mt-3 text-[11px] text-[var(--tg-text-muted)]">You can use the highlighted control while the tour is open.</p>
        <div className="mt-5 flex items-center justify-between gap-3">
          <button type="button" onClick={onClose} className="text-sm font-medium text-[var(--tg-text-muted)] hover:text-[var(--tg-text-primary)]">Skip tour</button>
          <div className="flex items-center gap-2">
            <span className="mr-1 text-xs text-[var(--tg-text-muted)]">{stepIndex + 1} / {steps.length}</span>
            {stepIndex > 0 && <Button type="button" variant="outline" size="sm" onClick={() => setStepIndex((current) => current - 1)}><ArrowLeft className="mr-1 h-4 w-4" />Back</Button>}
            <Button type="button" size="sm" onClick={() => (isLastStep ? onClose() : setStepIndex((current) => current + 1))}>
              {isLastStep ? <><Check className="mr-1 h-4 w-4" />Finish</> : <>Next<ArrowRight className="ml-1 h-4 w-4" /></>}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default MetaAgentTour;
